import http from './http';
import { getUserId } from 'utils/sessionStorage';

interface GetProgramListParams {
  status?: string;
  deadline?: string;
  category?: string;
  delivery?: string;
  page?: number;
  limit?: number;
}

interface UpdateLifecycleParams {
  lifecycle_status: string;
  application_status?: string;
  program_cycle_id?: number;
}

/** Master row for resource link type dropdown (GET api/masterdata/resource-links). */
export interface ResourceLinkMasterItem {
  key: string;
  label: string;
  description?: string;
}

/** Saved custom resource links on program manager metadata. */
export interface SavedResourceLink {
  typeKey: string;
  url: string;
  /** Denormalized display label; UI prefers masterdata by typeKey when present. */
  label?: string;
}

function normalizeResourceLinkMasterRow(x: any): ResourceLinkMasterItem | null {
  const key = x?.key ?? x?.slug ?? x?.id;
  if (key == null || String(key).trim() === '') return null;
  return {
    key: String(key),
    label: String(x?.label ?? x?.name ?? x?.title ?? 'Resource'),
    description: x?.description != null ? String(x.description) : undefined,
  };
}

const userProgramsService = {
  /**
   * Get list of user programs with filters and pagination
   */
  async getProgramList(params: GetProgramListParams = {}) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const queryParams: any = {};
    if (params.status) queryParams.status = params.status;
    if (params.deadline) queryParams.deadline = params.deadline;
    if (params.category) queryParams.category = params.category;
    if (params.delivery) queryParams.delivery = params.delivery;
    if (params.page) queryParams.page = params.page;
    if (params.limit) queryParams.limit = params.limit;

    const res = await http.get(`api/user-programs/student/${studentId}/list`, {
      params: queryParams,
    });
    return res;
  },

  /**
   * Get program details for manager view
   */
  async getProgramDetails(programId: number) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.get(`api/user-programs/${studentId}/${programId}/manager`);
    return res;
  },

  /**
   * Update program lifecycle status
   */
  async updateLifecycleStatus(programId: number, params: UpdateLifecycleParams) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.put(`api/user-programs/${studentId}/${programId}/lifecycle`, params);
    return res;
  },

  /**
   * Get programs manager summary (GET api/programs-manager/summary)
   * Returns programs with requirements and totals for summary cards.
   */
  async getProgramsManagerSummary() {
    const res = await http.get('api/programs-manager/summary');
    return res;
  },

  /**
   * Get list of program managers (GET api/programs-manager)
   * @param params optional: status, category, deadline (YYYY-MM-DD), delivery — only defined values are sent
   */
  async getProgramsManagerList(params?: { status?: string; category?: string; deadline?: string; delivery?: string }) {
    const query: Record<string, string> = {};
    if (params?.status?.trim()) query.status = params.status.trim();
    if (params?.category?.trim()) query.category = params.category.trim();
    if (params?.deadline?.trim()) query.deadline = params.deadline.trim();
    if (params?.delivery?.trim()) query.delivery = params.delivery.trim();
    const res = await http.get('api/programs-manager', { params: Object.keys(query).length ? query : undefined });
    return res;
  },

  /**
   * Add program to Program Manager (POST /api/programs-manager)
   */
  async addToProgramsManager(programId: number) {
    const res = await http.post('api/programs-manager', { programId });
    return res;
  },

  /**
   * Delete program manager (DELETE /api/programs-manager/:id)
   */
  async deleteProgramsManager(id: number) {
    const res = await http.delete(`api/programs-manager/${id}`);
    return res;
  },

  /**
   * Update program manager (PATCH /api/programs-manager/:id)
   * Body can include applicationForm, recommendationLetters, transcripts, and/or resume.
   */
  async patchProgramsManager(
    id: number,
    body: {
      /** Trạng thái pipeline (Application Status card: in_progress / submitted) — tách với application_form.status */
      status?: string;
      applicationForm?: {
        status: string;
        targetDate?: string;
        applicationDeadline?: string;
        link?: string;
        notes?: string;
      };
      recommendationLetters?: Array<{
        firstName: string;
        lastName: string;
        subjectRelationship: string;
        email?: string;
        status: string;
        date: string;
        link?: string;
      }>;
      transcripts?: Array<{
        type: string;
        status: string;
        date: string;
        link?: string;
        notes?: string;
      }>;
      resume?: Array<{
        documentName: string;
        status?: string;
        date: string;
        link?: string;
        notes?: string;
      }>;
      essays?: Array<{
        title: string;
        prompt?: string;
        status: string;
        targetDate: string;
        wordCount?: string;
        notes?: string;
        link?: string;
      }>;
      reminders?: Array<{
        title: string;
        dueDate: string;
        notes?: string;
        complete?: boolean;
      }>;
      metadata?: {
        notes?: string;
        resourceLinks?: SavedResourceLink[];
      };
    }
  ) {
    const res = await http.patch(`api/programs-manager/${id}`, body);
    return res;
  },

  /**
   * Master list for program resource link types (dropdown labels / keys).
   * GET api/masterdata/resource-links
   */
  async getMasterdataResourceLinks(): Promise<ResourceLinkMasterItem[]> {
    const res: any = await http.get('api/masterdata/resource-links');
    const raw = Array.isArray(res) ? res : res?.data ?? res?.items ?? res?.resourceLinks ?? [];
    const arr = Array.isArray(raw) ? raw : [];
    const rows: ResourceLinkMasterItem[] = [];
    for (let i = 0; i < arr.length; i++) {
      const n = normalizeResourceLinkMasterRow(arr[i]);
      if (n) rows.push(n);
    }
    return rows;
  },

  /**
   * Add a new program
   */
  async addProgram(programId: number, status: string = 'interested') {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.post('api/user-programs', {
      studentId: Number(studentId),
      programId: programId,
      status: status,
    });
    return res;
  },

  /**
   * Get requirements for a program
   */
  async getRequirements(programId: number) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.get(`api/user-programs/${studentId}/${programId}/requirements`);
    return res;
  },

  /**
   * Create a requirement
   */
  async createRequirement(programId: number, body: any) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.post(`api/user-programs/${studentId}/${programId}/requirements`, body);
    return res;
  },

  /**
   * Update a requirement
   */
  async updateRequirement(programId: number, requirementId: number, body: any) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.put(`api/user-programs/${studentId}/${programId}/requirements/${requirementId}`, body);
    return res;
  },

  /**
   * Delete a requirement
   */
  async deleteRequirement(programId: number, requirementId: number) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.delete(`api/user-programs/${studentId}/${programId}/requirements/${requirementId}`);
    return res;
  },

  /**
   * Get essays for a program
   */
  async getEssays(programId: number) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.get(`api/user-programs/${studentId}/${programId}/essays`);
    return res;
  },

  /**
   * Create an essay
   */
  async createEssay(programId: number, body: any) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.post(`api/user-programs/${studentId}/${programId}/essays`, body);
    return res;
  },

  /**
   * Update an essay
   */
  async updateEssay(programId: number, essayId: number, body: any) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.put(`api/user-programs/${studentId}/${programId}/essays/${essayId}`, body);
    return res;
  },

  /**
   * Delete an essay
   */
  async deleteEssay(programId: number, essayId: number) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.delete(`api/user-programs/${studentId}/${programId}/essays/${essayId}`);
    return res;
  },

  /**
   * Get reminders for a program
   */
  async getReminders(programId: number) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.get(`api/user-programs/${studentId}/${programId}/reminders`);
    return res;
  },

  /**
   * Create a reminder
   */
  async createReminder(programId: number, body: any) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.post(`api/user-programs/${studentId}/${programId}/reminders`, body);
    return res;
  },

  /**
   * Update a reminder
   */
  async updateReminder(programId: number, reminderId: number, body: any) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.put(`api/user-programs/${studentId}/${programId}/reminders/${reminderId}`, body);
    return res;
  },

  /**
   * Delete a reminder
   */
  async deleteReminder(programId: number, reminderId: number) {
    const studentId = getUserId();
    if (!studentId) {
      throw new Error('Student ID not found');
    }

    const res = await http.delete(`api/user-programs/${studentId}/${programId}/reminders/${reminderId}`);
    return res;
  },
};

export default userProgramsService;

